import { useCallback, useMemo, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  BackgroundVariant,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box,
  Button,
  ButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesome,
  Edit,
  Blender,
  Videocam,
  ClosedCaption,
  CloudUpload,
  Collections,
  TextFields,
  MergeType,
} from '@mui/icons-material';
import GenerateImageNode from './nodes/GenerateImageNode';
import EditImageNode from './nodes/EditImageNode';
import BlendNode from './nodes/BlendNode';
import VideoNode from './nodes/VideoNode';
import AssetNode from './nodes/AssetNode';
import UploadImageNode from './nodes/UploadImageNode';
import TextOverlayNode from './nodes/TextOverlayNode';
import CaptionNode from './nodes/CaptionNode';
import ConcatNode, { type ConcatNodeData } from './nodes/ConcatNode';

import {
  useCanvasStore,
  makeNodeId,
  type GenerateNodeData,
  type EditNodeData,
  type BlendNodeData,
  type VideoNodeData,
  type CaptionNodeData,
  type TextOverlayNodeData,
  type UploadNodeData,
  type StudioNodeData,
} from './useCanvasStore';
import { useStudioStore } from '../stores/studio.store';
import type { Node } from '@xyflow/react';

const NODE_TYPES = {
  generateImage: GenerateImageNode,
  editImage: EditImageNode,
  blend: BlendNode,
  video: VideoNode,
  caption: CaptionNode,
  textOverlay: TextOverlayNode,
  upload: UploadImageNode,
  asset: AssetNode,
  concat: ConcatNode,
};

const DEFAULT_DATA: Record<string, () => StudioNodeData> = {
  generateImage: (): GenerateNodeData => ({
    label: 'Generate Image',
    prompt: '',
    aspectRatio: '1:1',
  }),
  editImage: (): EditNodeData => ({
    label: 'Edit Image',
    instruction: '',
  }),
  blend: (): BlendNodeData => ({
    label: 'Blend Images',
    instruction: '',
  }),
  video: (): VideoNodeData => ({
    label: 'Generate Video',
    prompt: '',
    aspectRatio: '16:9',
    duration: '8s',
    status: 'idle',
    enhancePrompt: true,
  }),
  caption: (): CaptionNodeData => ({
    label: 'Auto Caption',
  }),
  concat: (): ConcatNodeData => ({
    label: 'Concat Videos',
    stripAudio: false,
  }),
  textOverlay: (): TextOverlayNodeData => ({
    label: 'Text Overlay',
    text: '',
    preset: 'headline',
    x: 50,
    y: 20,
    fontSize: 72,
    fontFamily: 'Montserrat',
    color: '#ffffff',
    backgroundColor: 'rgba(0,0,0,0.45)',
    strokeColor: '#000000',
    strokeWidth: 0,
  }),
  upload: (): UploadNodeData => ({
    label: 'Upload Image',
  }),
};

export default function StudioCanvas() {
  const nodes = useCanvasStore((s) => s.nodes);
  const edges = useCanvasStore((s) => s.edges);
  const onNodesChange = useCanvasStore((s) => s.onNodesChange);
  const onEdgesChange = useCanvasStore((s) => s.onEdgesChange);
  const onConnect = useCanvasStore((s) => s.onConnect);
  const isValidConnection = useCanvasStore((s) => s.isValidConnection);
  const addNode = useCanvasStore((s) => s.addNode);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rfInstance = useRef<ReactFlowInstance<any, any> | null>(null);

  const assets = useStudioStore((s) => s.assets);

  const handleAddNode = useCallback(
    (type: string) => {
      // Place new node at center of current viewport
      const position = rfInstance.current
        ? rfInstance.current.screenToFlowPosition({
            x: window.innerWidth / 2 - 140,
            y: window.innerHeight / 2 - 100,
          })
        : { x: 100 + Math.random() * 200, y: 100 + Math.random() * 200 };

      const node: Node<StudioNodeData> = {
        id: makeNodeId(type),
        type,
        position,
        data: DEFAULT_DATA[type](),
      };
      addNode(node);
    },
    [addNode]
  );

  // Load existing project assets onto canvas as their original node types
  const handleLoadAssets = useCallback(() => {
    // Build a dedupe set across every identifier a node might carry so we
    // never duplicate an asset that's already represented on the canvas.
    const existingIds = new Set<string>();
    for (const n of nodes) {
      const d = n.data as Record<string, unknown>;
      for (const key of ['assetId', 'serverId', 'gcsPath'] as const) {
        const v = d[key];
        if (typeof v === 'string' && v) existingIds.add(v);
      }
    }
    const newAssets = assets.filter((a) => {
      if (!a.serverId) return false;
      if (existingIds.has(a.serverId)) return false;
      if (a.gcsPath && existingIds.has(a.gcsPath)) return false;
      return true;
    });
    
    if (newAssets.length === 0) return;

    const startX = rfInstance.current
      ? rfInstance.current.screenToFlowPosition({ x: 100, y: 150 }).x
      : 50;
    const startY = rfInstance.current
      ? rfInstance.current.screenToFlowPosition({ x: 100, y: 150 }).y
      : 50;

    newAssets.forEach((asset, i) => {
      let node: Node<StudioNodeData>;
      const position = { x: startX + (i % 4) * 310, y: startY + Math.floor(i / 4) * 350 };

      if (asset.type === 'video') {
        node = {
          id: makeNodeId('video'),
          type: 'video',
          position,
          data: {
            label: 'Generate Video',
            prompt: asset.prompt || '',
            aspectRatio: '16:9',
            duration: '8s',
            status: 'completed',
            resultVideo: asset.data,
            assetId: asset.serverId,
          } as VideoNodeData,
        };
      } else {
        // Map tool to node type
        const tool = asset.tool || 'generate';
        const isEdit = tool === 'edit';
        const isBlend = tool === 'blend';
        const isUpload = tool === 'upload';

        if (isUpload) {
          node = {
            id: makeNodeId('upload'),
            type: 'upload',
            position,
            data: {
              label: 'Upload Image',
              resultImage: asset.data,
              resultMimeType: asset.mimeType,
              assetId: asset.serverId,
              originalName: asset.prompt || 'Uploaded image',
            } as UploadNodeData,
          };
        } else if (isEdit) {
          node = {
            id: makeNodeId('editImage'),
            type: 'editImage',
            position,
            data: {
              label: 'Edit Image',
              instruction: asset.prompt || '',
              resultImage: asset.data,
              resultMimeType: asset.mimeType,
              assetId: asset.serverId,
            } as EditNodeData,
          };
        } else if (isBlend) {
          node = {
            id: makeNodeId('blend'),
            type: 'blend',
            position,
            data: {
              label: 'Blend Images',
              instruction: asset.prompt || '',
              resultImage: asset.data,
              resultMimeType: asset.mimeType,
              assetId: asset.serverId,
            } as BlendNodeData,
          };
        } else {
          node = {
            id: makeNodeId('generateImage'),
            type: 'generateImage',
            position,
            data: {
              label: 'Generate Image',
              prompt: asset.prompt || '',
              aspectRatio: '1:1',
              resultImage: asset.data,
              resultMimeType: asset.mimeType,
              assetId: asset.serverId,
            } as GenerateNodeData,
          };
        }
      }

      addNode(node);
    });
  }, [assets, nodes, addNode]);

  const nodeTypes = useMemo(() => NODE_TYPES, []);

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        isValidConnection={isValidConnection}
        nodeTypes={nodeTypes}
        onInit={(instance: ReactFlowInstance) => { rfInstance.current = instance; }}
        fitView
        snapToGrid
        snapGrid={[16, 16]}
        deleteKeyCode={['Backspace', 'Delete']}
        proOptions={{ hideAttribution: true }}
        style={{ backgroundColor: '#121212' }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#333" />
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={(n) => {
            switch (n.type) {
              case 'generateImage': return '#7c4dff';
              case 'editImage': return '#00897b';
              case 'blend': return '#e65100';
              case 'video': return '#1565c0';
              case 'textOverlay': return '#e65100';
              default: return '#666';
            }
          }}
          maskColor="rgba(0,0,0,0.7)"
          style={{ background: '#1e1e1e' }}
        />

        {/* Add node toolbar */}
        <Panel position="top-center">
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 3,
              px: 1,
              py: 0.5,
            }}
          >
            <ButtonGroup size="small" variant="outlined">
              <Tooltip title="Generate Image (text → image)" arrow>
                <Button
                  startIcon={<AutoAwesome sx={{ fontSize: 16 }} />}
                  onClick={() => handleAddNode('generateImage')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Image
                </Button>
              </Tooltip>
              <Tooltip title="Edit Image (modify existing)" arrow>
                <Button
                  startIcon={<Edit sx={{ fontSize: 16 }} />}
                  onClick={() => handleAddNode('editImage')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Edit
                </Button>
              </Tooltip>
              <Tooltip title="Blend (combine multiple images)" arrow>
                <Button
                  startIcon={<Blender sx={{ fontSize: 16 }} />}
                  onClick={() => handleAddNode('blend')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Blend
                </Button>
              </Tooltip>
              <Tooltip title="Generate Video (text/image → video)" arrow>
                <Button
                  startIcon={<Videocam sx={{ fontSize: 16 }} />}
                  onClick={() => handleAddNode('video')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Video
                </Button>
              </Tooltip>
              <Tooltip title="Concat Videos (merge multiple videos)" arrow>
                <Button
                  startIcon={<MergeType sx={{ fontSize: 16 }} />}
                  onClick={() => handleAddNode('concat')}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Concat
                </Button>
              </Tooltip>
              <Tooltip title="Auto Caption (transcribe + burn subtitles)" arrow>
                <Button
                  startIcon={<ClosedCaption sx={{ fontSize: 16 }} />}
                  onClick={() => handleAddNode('caption')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Caption
                </Button>
              </Tooltip>
              <Tooltip title="Text Overlay (add text to image)" arrow>
                <Button
                  startIcon={<TextFields sx={{ fontSize: 16 }} />}
                  onClick={() => handleAddNode('textOverlay')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Text
                </Button>
              </Tooltip>
              <Tooltip title="Upload Image (from your computer)" arrow>
                <Button
                  startIcon={<CloudUpload sx={{ fontSize: 16 }} />}
                  onClick={() => handleAddNode('upload')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Upload
                </Button>
              </Tooltip>
            </ButtonGroup>
            {assets.length > 0 && (
              <Tooltip title="Load project assets onto canvas" arrow>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<Collections sx={{ fontSize: 16 }} />}
                  onClick={handleLoadAssets}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', ml: 1 }}
                >
                  Assets ({assets.length})
                </Button>
              </Tooltip>
            )}
          </Box>
        </Panel>

        {/* Help text when empty */}

        {nodes.length === 0 && (
          <Panel position="top-center" style={{ top: '40%' }}>
            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="h6" gutterBottom>
                Creative Studio Canvas
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Add nodes from the toolbar above, then connect them to build your creative pipeline.
              </Typography>
              <Typography variant="caption" display="block">
                🖼️ Generate images → 🔗 Wire as start/end frames → 🎬 Generate video
              </Typography>
            </Box>
          </Panel>
        )}
      </ReactFlow>
    </Box>
  );
}
